/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useEffect, useState} from "react";

import {useDispatch} from "react-redux";
import useTypedSelector from "@Services/Hooks/UseTypedSelector";

import APPLICATION_ROUTER_INTERFACE from "@ROUTER/Interfaces/Router";
import ApplicationRouterContext from "@Contexts/Router";

import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";


import {Switch, Route, Redirect} from "react-router-dom";
import DISPOSITION_INTERFACE from "@Services/Application/Interfaces/Dispositions/Disposition";
import ERROR_SYSTEM_MESSAGE_INTERFACE from "@Services/Application/Interfaces/_Messages/Errors";

import ApplicationApiContext from "@Contexts/Api";
import BlockContentContainerComponent from "@Components/UI/Containers/Block";
import BlockContentHeaderComponent from "@Components/UI/Containers/Block/Header";
import {Col, Row} from "react-bootstrap";
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";
import HTTP_ERROR_RESPONSE_INTERFACE from "@Services/Interfaces/Http/Response/Error";
import DEFAULT_REQUEST_HEADERS from "@Services/Interfaces/Http/Request/Headers";
import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";
import dateFormat from "dateformat";
import PRODUCT_INTERFACE from "@Services/Application/Interfaces/Products/Product";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";
import CHANGE_API_REQUEST_TOKEN_ACTION from "@Actions/Api/ChangeApiRequestTokenValue";
import prepareRestApiRequestToken from "@Services/Utilities/RequestToken/Prepare";


type DispositionPageProps = {match: any};


export const DispositionPageComponent: React.FC<DispositionPageProps> = ({match}): JSX.Element | null => {


    const DISPOSITION_ID: string | null = match?.params?.dispositionId || null;


    const DISPATCHER = useDispatch();


    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state):ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;


    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    const ROUTER: APPLICATION_ROUTER_INTERFACE = useContext(ApplicationRouterContext);


    const [DISPOSITION_DATA, SET_DISPOSITION_DATA] = useState<DISPOSITION_INTERFACE | null>(null);
    const [DISPOSITION_ERROR, SET_DISPOSITION_ERROR] = useState<ERROR_SYSTEM_MESSAGE_INTERFACE | null>(null);

    useEffect(() => {

        if (!DISPOSITION_ID || !REST_API_URL || !LOGGED_USER?.userID) return;

        const DISPOSITION_API_URL = `${REST_API_URL}/Disp/Get`;

        const DISPOSITION_REQUEST_DATA = {
            "UserId": LOGGED_USER?.userID, "DispNo": DISPOSITION_ID.replaceAll("--", "/")
        };

        const GET_DISPOSITION_DATA = async () => {

            DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(true));

            try {

                const DISPOSITION_RESPONSE = await REST_API_REQUEST(
                    DISPOSITION_API_URL,
                    DISPOSITION_REQUEST_DATA,
                    REST_API_REQUEST_TOKEN
                );

                DISPATCHER(CHANGE_API_REQUEST_TOKEN_ACTION(
                    prepareRestApiRequestToken(DISPOSITION_RESPONSE.headers)
                ));

                if (DISPOSITION_RESPONSE.status === 200) {
                    const RESPONSE_CONTENT: DISPOSITION_INTERFACE = await DISPOSITION_RESPONSE.json();
                    SET_DISPOSITION_DATA(RESPONSE_CONTENT);
                    SET_DISPOSITION_ERROR(null);

                } else {
                    const RESPONSE_CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await DISPOSITION_RESPONSE.json();
                    SET_DISPOSITION_ERROR({ErrorCode: DISPOSITION_RESPONSE.status, UserMessage: RESPONSE_CONTENT.UserMessage});
                }

            } catch (e) {
                SET_DISPOSITION_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            }

            DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(false));

        };

        GET_DISPOSITION_DATA();

        return () => {
            SET_DISPOSITION_DATA(null);
            SET_DISPOSITION_ERROR(null);
        };

    }, [DISPOSITION_ID, REST_API_URL]);


    if (!LOGGED_USER || !LOGGED_USER?.userID) return <Redirect to={ROUTER.COMMON.HOME.URL} />;


    if (!DISPOSITION_ID) return <Redirect to={ROUTER.AUTHENTICATED.DISPOSITIONS.DISPOSITIONS_TERMINALS_LIST.URL} />;


    return (
        <BlockContentContainerComponent cssClasses={"disposition-page-component disposition-page"}>

            <BlockContentHeaderComponent headerText={`Dyspozycja nr ${DISPOSITION_ID?.replaceAll("--", "/")}`} />

            {!!DISPOSITION_ERROR &&
            <ErrorMessageComponent messageText={DISPOSITION_ERROR.UserMessage} />
            }

            {DISPOSITION_DATA &&
            <Col xs={12} className={"disposition-data-component disposition-data"}>
                <Row className={"disposition-data-content"}>

	                <Col xs={12} className={"disposition-information-component disposition-information"}>
		                <Row className={"disposition-information-content"}>
			                <Col xs={12} className={"disposition-terminal"}>
				                <span>{DISPOSITION_DATA?.TerminalMasterNo}</span>
			                </Col>
			                <Col xs={12} className={"disposition-valid-date"}>
				                <span className={"valid-from-label"}>{`Ważna od`}</span>
				                <span className={"valid-from-date"}>{dateFormat(DISPOSITION_DATA.ValidFrom, "dd-mm-yyyy")}</span>
				                <span className={"valid-to-label"}>{`do`}</span>
				                <span className={"valid-to-date"}>{dateFormat(DISPOSITION_DATA.ValidTo, "dd-mm-yyyy")}</span>
			                </Col>
		                </Row>
	                </Col>

                    {!!DISPOSITION_DATA.IsBooked &&
	                <Col xs={12} className={"disposition-is-booked-component disposition-is-booked"}>
	                    <Row className={"disposition-is-booked-content"}>
                            <Col xs={12} className={"disposition-is-booked-status"}>
                                <span>{`Zaawizowana`}</span>
                            </Col>
                            <Col xs={12} className={"disposition-process-start-date"}>
                                <span>{`Godzina rozpoczęcia nalewu`}:</span>
                                <span>{dateFormat(DISPOSITION_DATA.ProcessStartDate, "dd-mm-yyyy HH:MM")}</span>
                            </Col>
                        </Row>
	                </Col>
                    }

                    {!!DISPOSITION_DATA.DispItems && DISPOSITION_DATA.DispItems.length > 0 &&
	                <Col xs={12} className={"disposition-products-component disposition-products"}>
		                <Row className={"disposition-products-content"}>

			                <Col xs={12} className={"disposition-products-header"}>
				                <span>{`Produkty`}</span>
			                </Col>

                            {DISPOSITION_DATA.DispItems.map((PRODUCT: PRODUCT_INTERFACE) =>
                            <Col key={PRODUCT.ProductMasterNo} xs={12} className={"disposition-product-component disposition-product"}>
                                <Row className={"disposition-product-content"}>
                                    <Col xs={12} className={"disposition-product-name"}>
                                        <span>{PRODUCT.ProductSalesName || PRODUCT.ProductShortName}</span>
                                    </Col>
                                    {!!PRODUCT.SENTNumber &&
	                                <Col xs={12} className={"disposition-product-sent"}>
		                                <span>{PRODUCT.SENTNumber}</span>
	                                </Col>
                                    }
                                    <Col xs={12} className={"disposition-product-information"}>
                                        <span className={"information-label"}>{`Numer`}:</span>
                                        <span className={"information-value"}>{PRODUCT.ProductMasterNo}</span>
                                    </Col>
                                    <Col xs={12} className={"disposition-product-information"}>
                                        <span className={"information-label"}>{`Ilość`}:</span>
                                        <span className={"information-value"}>{PRODUCT.Qty} {PRODUCT.QtyUnitName}</span>
                                    </Col>
                                    <Col xs={12} className={"disposition-product-information"}>
                                        <span className={"information-label"}>{`Klient`}:</span>
                                        <span className={"information-value"}>{PRODUCT.ClientName}</span>
                                    </Col>
                                    <Col xs={12} className={"disposition-product-information"}>
                                        <span className={"information-label"}>{`Miejsce Dostawy`}:</span>
                                        <span className={"information-value"}>{PRODUCT.ReceiverName}</span>
                                    </Col>
                                </Row>
                            </Col>
                            )}

		                </Row>
	                </Col>
                    }

                </Row>
            </Col>
            }

        </BlockContentContainerComponent>
    );

};


export default DispositionPageComponent;
